import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, ClockIcon, BookOpenIcon } from '@heroicons/react/solid'

import SEOHead from '../utilities/SEOHead'
import { ItemListSchema, BreadcrumbSchema } from '../utilities/StructuredData'
import MarketingNavBar from '../leevo_ui/NavBar/MarketingNavBar/MarketingNavBar'
import MarketingPageFooter from '../leevo_ui/MarketingPageFooter/MarketingPageFooter'
import SimpleJustifiedCTA from '../leevo_ui/SimpleJustifiedCTA/SimpleJustifiedCTA'
import { getAllArticles, getFeaturedArticles } from '../../content/blog'

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'guides', label: 'Guides' },
  { id: 'sports', label: 'Sport-Specific' },
  { id: 'growth', label: 'Growth' },
  { id: 'tips', label: 'Tips' },
  { id: 'business', label: 'Business' }
]

const CATEGORY_COLOR = 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'

function FeaturedArticleCard({ article }) {
  return (
    <Link to={`/blog/${article.slug}`} className="group relative block overflow-hidden rounded-2xl bg-gray-900">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/90 to-purple-900/90" />
      <div className="relative p-8 lg:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${CATEGORY_COLOR}`}>{article.category}</span>
          <span className="flex items-center gap-1 text-sm text-purple-200">
            <ClockIcon className="w-4 h-4" />
            {article.readTime}
          </span>
        </div>
        <h3 className="text-2xl lg:text-3xl font-bold text-white group-hover:text-purple-200 transition-colors text-balance">
          {article.title}
        </h3>
        <p className="mt-4 text-purple-100 text-lg line-clamp-2">{article.description}</p>
        <div className="mt-6 inline-flex items-center text-white font-semibold group-hover:gap-3 gap-2 transition-all">
          Read article
          <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  )
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex flex-col h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-lg dark:hover:shadow-purple-900/20 transition-all"
    >
      <div className="flex-1 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${CATEGORY_COLOR}`}>{article.category}</span>
          <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <ClockIcon className="w-3.5 h-3.5" />
            {article.readTime}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2">{article.title}</h3>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 line-clamp-3">{article.description}</p>
      </div>
      <div className="px-6 pb-6">
        <span className="inline-flex items-center text-sm font-semibold text-purple-600 dark:text-purple-400 group-hover:gap-2 gap-1 transition-all">
          Read more
          <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  )
}

export default function BlogIndexPage() {
  const [selectedCategory, setSelectedCategory] = useState('all')

  const allArticles = getAllArticles()
  const featuredArticles = getFeaturedArticles()

  const filteredArticles = useMemo(() => {
    if (selectedCategory === 'all') {
      return allArticles
    }
    return allArticles.filter((article) => article.category === selectedCategory)
  }, [selectedCategory, allArticles])

  const nonFeaturedArticles = useMemo(() => {
    if (selectedCategory === 'all') {
      return filteredArticles.filter((article) => !article.featured)
    }
    return filteredArticles
  }, [selectedCategory, filteredArticles])

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' }
  ]

  const articleListItems = allArticles.map((article) => ({
    name: article.title,
    description: article.description,
    url: `/blog/${article.slug}`
  }))

  return (
    <>
      <SEOHead
        title="Blog - Sports Program Management Guides and Tips"
        description="Expert guides, tips, and best practices for running successful sports programs. Learn about registration, skill tracking, parent communication, and growing your program."
        path="/blog"
        keywords={[
          'sports program guides',
          'sports management tips',
          'class registration best practices',
          'skill tracking guide',
          'sports business resources',
          'youth sports management'
        ]}
      />
      <ItemListSchema name="Leevo Blog" description="Guides and articles for sports program management" items={articleListItems} />
      <BreadcrumbSchema items={breadcrumbs} />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <MarketingNavBar />

        <main>
          <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900">
            <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:py-24">
              <div className="flex items-center justify-center gap-3 mb-6">
                <BookOpenIcon className="w-10 h-10 text-purple-200" />
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl text-center text-balance">
                The Leevo Blog
              </h1>
              <p className="max-w-2xl mx-auto mt-6 text-xl text-purple-100 text-center text-balance">
                Expert guides and insights to help you build and grow a successful sports program.
              </p>
            </div>
          </section>

          {selectedCategory === 'all' && featuredArticles.length > 0 && (
            <section className="py-12 lg:py-16 bg-gray-50 dark:bg-gray-900">
              <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <h2 className="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-6">
                  Featured
                </h2>
                <div className="grid gap-6 lg:grid-cols-2">
                  {featuredArticles.slice(0, 2).map((article) => (
                    <FeaturedArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="py-12 lg:py-16 bg-white dark:bg-gray-800">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">All Articles</h2>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === category.id
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {nonFeaturedArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>

              {nonFeaturedArticles.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-gray-500 dark:text-gray-400">No articles found in this category.</p>
                </div>
              )}
            </div>
          </section>

          <SimpleJustifiedCTA showTrialButton />
        </main>

        <MarketingPageFooter />
      </div>
    </>
  )
}
